import { AnimatePresence, motion } from 'motion/react'
import { type ReactNode, useState } from 'react'

const sidepanelVariants = {
    open: {
        opacity: 1,
        width: 'fit-content',
    },
    closed: {
        opacity: 0,
        width: 0,
    },
}

export type AstraPageContentLayoutProps = {
    /**
     * Optional borderless, full-width navigation bar rendered at the top of the page.
     */
    navigationHeader?: ReactNode
    /**
     * Optional headers (e.g. a filter/control bar) rendered inside the main content column,
     * above the scrollable content and aligned with it, so they do not span the side panel.
     */
    contentHeaders?: ReactNode[]
    /** Main content area */
    content: ReactNode
    /** Optional side panel content */
    sidepanel?: ReactNode
    /** Controls whether the sidepanel is visible */
    isSidepanelOpen?: boolean
    /** Additional CSS classes for the root container */
    className?: string
}

const MAIN_CONTENT_ID = 'HERO_PAGE_CONTENT_LAYOUT_MAIN'

/**
 * AstraPageContentLayout provides a flexible page content structure with:
 * - A borderless, full-width navigation bar at the top (`navigationHeader`)
 * - Optional in-content bars (`contentHeaders`) that sit above the content, aligned with it
 * - A main content area that grows to fill available space
 * - An optional animated sidepanel for details or auxiliary content
 *
 * This component is designed to be used inside AstraMainLayout as the page content.
 * It works well with WithFilterPane for collapsible filter sections and Bar for toolbar layouts.
 */
export const AstraPageContentLayout = ({ content, navigationHeader, contentHeaders, sidepanel, isSidepanelOpen, className }: AstraPageContentLayoutProps) => {
    const [isAnimationComplete, setIsAnimationComplete] = useState(false)

    return (
        <div className={`flex h-full w-full flex-col ${className ?? ''} `}>
            {navigationHeader && <div className="shrink pb-3 pt-2">{navigationHeader}</div>}

            <div className={`flex grow overflow-hidden ${isSidepanelOpen ? 'gap-4' : ''}`}>
                <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-surface-base">
                    {contentHeaders?.map((header, index) => (
                        <div key={`content-header-${index}`} className="shrink border-b px-4 py-4">
                            {header}
                        </div>
                    ))}
                    <div id={MAIN_CONTENT_ID} className="transition-margin w-full flex-1 overflow-y-auto">
                        {content}
                    </div>
                </div>
                {sidepanel && (
                    <AnimatePresence>
                        <motion.div
                            initial={false}
                            animate={isSidepanelOpen ? 'open' : 'closed'}
                            variants={sidepanelVariants}
                            transition={{ duration: 0.2 }}
                            onAnimationStart={() => setIsAnimationComplete(false)}
                            onAnimationComplete={() => setIsAnimationComplete(true)}
                            className={`overflow-hidden`}
                        >
                            {sidepanel}
                        </motion.div>
                    </AnimatePresence>
                )}
                {isAnimationComplete && isSidepanelOpen && <div data-test="sidebar-loaded" className="hidden" />}
            </div>
        </div>
    )
}
